import { useEffect, useRef, useState } from "react";

type HoverBoxPosition = {
  xPosition: number | undefined;
  yPosition: number | undefined;
  borderRadius: string | undefined;
};

// Mouse hover follows the cursor (matches how tooltips are expected to behave with a
// pointer); keyboard focus has no cursor to follow, so it anchors to the focused element's
// own rect instead, centered vertically and flush against it. Both flip-to-fit checks are
// against the chart container's own edges (boundingClientRect), not window.innerWidth/Height —
// the container can be narrower than the viewport (scrolled/clipped), so a window-relative
// check let the box render off the visible edge of the chart with room to spare in the browser
// window.
const useHoverBoxPosition = (
  hoverTargetKey: string | undefined,
  containerElementId: string,
  tooltipElementId: string,
): HoverBoxPosition => {
  const isRun = useRef(false);
  const [xPosition, setXPosition] = useState<number>();
  const [yPosition, setYPosition] = useState<number>();
  const [boundingClientRect, setBoundingClientRect] = useState<DOMRect>();
  const [containerElement, setContainerElement] = useState<HTMLElement | null>(
    null,
  );
  const [borderRadius, setBorderRadius] = useState<string>();

  useEffect(() => {
    isRun.current = false;
  }, [hoverTargetKey]);

  const handleMouseMove = (e: MouseEvent) => {
    if (!boundingClientRect) return;

    const tooltipElement = document.getElementById(tooltipElementId);
    const tooltipHeight = tooltipElement?.clientHeight ?? 0;
    const tooltipWidth = tooltipElement?.clientWidth ?? 0;

    const showAbove =
      boundingClientRect.bottom - e.clientY - tooltipHeight <= 0;
    const showToTheLeft =
      boundingClientRect.right - e.clientX - tooltipWidth <= 0;

    if (showAbove && showToTheLeft) {
      setBorderRadius(
        "var(--spacing-md) var(--spacing-md) 0 var(--spacing-md)",
      );
    } else if (showAbove && !showToTheLeft) {
      setBorderRadius(
        "var(--spacing-md) var(--spacing-md) var(--spacing-md) 0",
      );
    } else if (!showAbove && showToTheLeft) {
      setBorderRadius(
        "var(--spacing-md) 0 var(--spacing-md) var(--spacing-md)",
      );
    } else {
      setBorderRadius(
        "0 var(--spacing-md) var(--spacing-md) var(--spacing-md)",
      );
    }

    const offSetHeight = showAbove ? -(tooltipHeight + 15) : 5;
    const offSetRight = showToTheLeft ? -(tooltipWidth + 15) : 5;

    setXPosition(e.clientX - boundingClientRect.left + offSetRight);
    setYPosition(e.clientY - boundingClientRect.top + offSetHeight);
    isRun.current = true;
  };

  useEffect(() => {
    setContainerElement(document.getElementById(containerElementId));
  }, [containerElementId]);

  useEffect(() => {
    if (containerElement) {
      setBoundingClientRect(containerElement.getBoundingClientRect());
    }
  }, [containerElement]);

  useEffect(() => {
    if (!hoverTargetKey || !boundingClientRect || !containerElement) return;

    const focusedElement = document.activeElement as HTMLElement | null;
    if (!focusedElement || !containerElement.contains(focusedElement)) return;

    const focusedElementRect = focusedElement.getBoundingClientRect();
    const tooltipHeight =
      document.getElementById(tooltipElementId)?.clientHeight ?? 0;
    const tooltipWidth =
      document.getElementById(tooltipElementId)?.clientWidth ?? 0;
    const showToTheRight =
      focusedElementRect.right + tooltipWidth <= boundingClientRect.right;

    const rawX = showToTheRight
      ? focusedElementRect.right
      : focusedElementRect.left - tooltipWidth;
    const centeredY =
      focusedElementRect.top +
      focusedElementRect.height / 2 -
      tooltipHeight / 2;
    const clampedY = Math.min(
      Math.max(centeredY, boundingClientRect.top),
      boundingClientRect.bottom - tooltipHeight,
    );

    setXPosition(rawX - boundingClientRect.left);
    setYPosition(clampedY - boundingClientRect.top);
    setBorderRadius(
      "var(--spacing-md) var(--spacing-md) var(--spacing-md) var(--spacing-md)",
    );
    isRun.current = true;
  }, [hoverTargetKey, boundingClientRect, containerElement, tooltipElementId]);

  useEffect(() => {
    if (boundingClientRect) {
      containerElement!.addEventListener("mousemove", handleMouseMove);
    }
    return () => {
      if (boundingClientRect) {
        containerElement!.removeEventListener("mousemove", handleMouseMove);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundingClientRect]);

  return { xPosition, yPosition, borderRadius };
};

export default useHoverBoxPosition;
